import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, articleContent } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? articles.find(a => a.slug === slug) : null;
  const content = slug ? articleContent[slug] : null;

  useEffect(() => {
    if (!content || !article) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.cfd';

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Startseite',
              item: `${siteUrl}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.title,
              item: `${siteUrl}/artikel/${article.slug}`,
            },
          ],
        },
        {
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
          inLanguage: 'de-DE',
          datePublished: '2026-07-16',
          dateModified: article.updatedDate,
          author: {
            '@type': 'Organization',
            name: 'OOPBUY Spreadsheet',
          },
          publisher: {
            '@type': 'Organization',
            name: 'OOPBUY Spreadsheet',
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/artikel/${article.slug}`,
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: content.faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [content, article]);

  if (!article || !content) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="text-3xl font-bold text-foreground-950 mb-4">Artikel nicht gefunden</h1>
          <p className="text-foreground-500 mb-6">Der gesuchte Artikel existiert leider nicht.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Zurück zur Startseite
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer font-medium"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="hidden sm:inline">Zurück zur Startseite</span>
            <span className="sm:hidden">Startseite</span>
          </Link>
          <span className="text-sm text-foreground-400">OOPBUY Spreadsheet</span>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">Startseite</Link>
          <span>/</span>
          <span className="text-foreground-600">{article.title}</span>
        </nav>

        <header className="mb-12">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-5">
            <i className="ri-article-line"></i>
            SEO Guide
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
            {article.title}
          </h1>
          <p className="text-lg text-foreground-500 mb-6 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readingTime} Minuten Lesezeit
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Aktualisiert am {article.updatedDate}
            </span>
          </div>
        </header>

        <div className="mb-12 p-6 md:p-8 bg-background-100 rounded-2xl">
          <h2 className="text-lg font-semibold text-foreground-900 mb-4">Inhaltsverzeichnis</h2>
          <ul className="space-y-2">
            {content.sections.map((section, idx) => (
              <li key={idx}>
                <a
                  href={`#section-${idx}`}
                  className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <span className="text-primary-400 text-xs">{idx + 1}.</span>
                  {section.heading}
                </a>
                {section.subSections && (
                  <ul className="ml-6 mt-1.5 space-y-1">
                    {section.subSections.map((sub, subIdx) => (
                      <li key={subIdx}>
                        <a
                          href={`#section-${idx}-${subIdx}`}
                          className="text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          {sub.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        {content.sections.map((section, idx) => (
          <section key={idx} id={`section-${idx}`} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
              {section.heading}
            </h2>
            {section.content.split('\n\n').map((paragraph, pIdx) => (
              <p key={pIdx} className="text-base text-foreground-600 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}

            {section.subSections && section.subSections.map((sub, subIdx) => (
              <div key={subIdx} id={`section-${idx}-${subIdx}`} className="mt-6 ml-0 md:ml-6 pl-0 md:pl-6 md:border-l-2 border-background-200">
                <h3 className="text-xl font-bold text-foreground-900 mb-3">
                  {sub.heading}
                </h3>
                {sub.content.split('\n\n').map((paragraph, pIdx2) => (
                  <p key={pIdx2} className="text-base text-foreground-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </section>
        ))}

        <section className="mb-12 p-8 bg-background-100 rounded-2xl">
          <h2 className="text-2xl font-bold text-foreground-950 mb-6">
            Häufig gestellte Fragen (FAQ)
          </h2>
          <div className="space-y-4">
            {content.faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-xl p-5 cursor-pointer">
                <summary className="flex items-center justify-between text-base font-semibold text-foreground-800 group-open:text-primary-600 transition-colors list-none">
                  {faq.question}
                  <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                  <i className="ri-subtract-line hidden group-open:block text-primary-500"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-foreground-950 mb-5">
            Weitere Guides entdecken
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {articles
              .filter(a => a.slug !== slug)
              .slice(0, 6)
              .map(a => (
                <Link
                  key={a.slug}
                  to={`/artikel/${a.slug}`}
                  className="block p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                    {a.title}
                  </h3>
                  <span className="text-xs text-foreground-400">{a.readingTime} Min.</span>
                </Link>
              ))}
          </div>
        </section>

        <div className="p-8 md:p-10 bg-primary-500 rounded-2xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Bereit für dein nächstes Shopping-Erlebnis?
          </h2>
          <p className="text-base text-background-50/80 mb-6 max-w-lg mx-auto leading-relaxed">
            Entdecke tausende Produkte auf kaspreadsheet.com – mit den besten Bewertungen und sichersten Verkäufern.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-colors"
            >
              Jetzt entdecken
              <i className="ri-arrow-right-line"></i>
            </a>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-3.5 text-background-50/80 hover:text-background-50 text-sm font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200">
          <p className="text-xs text-foreground-400 leading-relaxed">
            <strong className="text-foreground-500">Haftungsausschluss:</strong> OOPBUY Spreadsheet ist eine unabhängige Informationsplattform. Wir stehen in keiner direkten Verbindung zu den genannten Marktplätzen oder Marken. Alle Informationen dienen ausschließlich Bildungszwecken. Für Käufe gelten die AGB der jeweiligen Plattform.
          </p>
        </div>
      </article>

      <footer className="bg-primary-800 text-background-50/40 text-center py-8 text-xs">
        <div className="max-w-4xl mx-auto px-6">
          <p>© OOPBUY Spreadsheet 2026 · Unabhängige Informationsplattform · <Link to="/" className="hover:text-background-50/60 transition-colors cursor-pointer underline">Startseite</Link></p>
        </div>
      </footer>
    </main>
  );
}